import cn from 'classnames';
import RBush from 'rbush';

import { getIcons } from './markups/icon/getIcons';
import * as styles from './styles.css';
import { IRBushItem, IFeatureProperties, TFeature } from '../../../types/map';
import { EMapItemZIndex } from '../../mapItemZIndex';
import { beautify } from '../../number';

interface IBalloonManagerOptions {
  map: YMaps.Map;
  rbush: RBush<IRBushItem>;
}

export function defineBalloonManager(ymaps: YMaps.IYMaps) {
  if (ymaps.modules.isDefined('BalloonManager')) {
    return;
  }

  ymaps.modules.define<
    [YMaps.ITemplateLayoutFactory, typeof YMaps.shape.Rectangle, typeof YMaps.geometry.pixel.Rectangle]
  >(
    'BalloonManager',
    ['templateLayoutFactory', 'shape.Rectangle', 'geometry.pixel.Rectangle'],
    (
      provide,
      templateLayoutFactory: YMaps.ITemplateLayoutFactory,
      shapeRectangle: typeof YMaps.shape.Rectangle,
      geometryPixelRectangle: typeof YMaps.geometry.pixel.Rectangle,
    ) => {
      class BalloonManager {
        public readonly objectManager: YMaps.ObjectManager;

        private map: YMaps.Map;
        private rbush: RBush<IRBushItem>;

        public constructor(options: IBalloonManagerOptions) {
          this.map = options.map;
          this.rbush = options.rbush;
          this.objectManager = new ymaps.ObjectManager();
        }

        public updateBalloons = () => {
          const bounds = this.map.getBounds();
          const rBushItems = this.rbush.search({
            minX: bounds[0][0],
            minY: bounds[0][1],
            maxX: bounds[1][0],
            maxY: bounds[1][1],
          });

          this.objectManager.removeAll();

          this.objectManager.add({
            type: 'FeatureCollection',
            features: rBushItems.map(this.prepareBalloon).filter(feature => !!feature) as TFeature[],
          });
        };

        public updateBalloonProperties = (featureId: string, properties: IFeatureProperties) => {
          this.objectManager.objects.setObjectOptions(featureId, {
            iconLayout: this.prepareBalloonLayout(properties as IFeatureProperties),
            zIndex: properties.isActive ? EMapItemZIndex.BALLOON_ACTIVE : EMapItemZIndex.BALLOON_DEFAULT,
          });
        };

        private prepareBalloon = ({ feature }: IRBushItem) => {
          const { properties } = feature;

          if (!properties.offers[0].averagePricePerDay) {
            return null;
          }

          return {
            ...feature,
            options: {
              iconLayout: this.prepareBalloonLayout(properties as IFeatureProperties),
              zIndex: properties.isActive ? EMapItemZIndex.BALLOON_ACTIVE : EMapItemZIndex.BALLOON_DEFAULT,
            },
          };
        };

        private prepareBalloonLayout = (properties: IFeatureProperties) => {
          const content = this.prepareBalloonContent(properties);

          return templateLayoutFactory.createClass(content, {
            getShape() {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const element: HTMLElement = (this as any).getElement();
              if (!element) {
                return null;
              }

              const wrapperElement = element.firstElementChild;
              if (!wrapperElement) {
                return null;
              }

              const boundingRect = wrapperElement.getBoundingClientRect();
              const offsetX = -boundingRect.width / 2;
              const offsetY = -boundingRect.height - 4 + 12; // 12 – сдвиг вниз, чтобы скрыть пин и встать на его место

              return new shapeRectangle(
                new geometryPixelRectangle([
                  [offsetX, offsetY],
                  [offsetX + boundingRect.width, offsetY + boundingRect.height],
                ]),
              );
            },
          });
        };

        private prepareBalloonContent(feature: IFeatureProperties) {
          const { isActive, offers } = feature;
          const { isViewed, testData, isFavorite, averagePricePerDay } = offers[0] || {};
          const testDataAttributes = testData
            ? Object.entries(testData)
                .map(([name, value]) => `${name}="${value}"`)
                .join(' ')
            : '';

          const price = beautify(averagePricePerDay) + ' ₽';
          const icon = getIcons(feature);

          return `
            <div
              class="${cn(styles['pin'], isViewed && styles['viewed'], isActive && styles['active'])}"
              data-testid="pin"
              data-is-active="${Boolean(isActive)}"
              data-is-viewed="${Boolean(isViewed)}"
              data-is-favorite="${Boolean(isFavorite)}"
              ${testDataAttributes}
            >
              <div class="${styles['balloon']}">
                <div class="${styles['price']}">${price}</div>
                ${icon}
              </div>
            </div>
          `;
        }
      }

      provide(BalloonManager);
    },
  );
}
